import React, { useEffect, useState } from 'react';
import {
  Dialog, Box, Stack, Typography, Button, CircularProgress,
} from '@mui/material';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import axios from 'axios';
import { loadStripe } from '@stripe/stripe-js';
import {
  dataFetcher, baseUrl, getToken, STRIPE_PUBLIC_KEY,
} from 'Api';

// Load Stripe once (null if no key configured) — reused only for the 3DS step.
const stripePromise = STRIPE_PUBLIC_KEY ? loadStripe(STRIPE_PUBLIC_KEY) : null;

const money = (cents, currency = 'usd') => ((Number(cents) || 0) / 100)
  .toLocaleString('en-US', { style: 'currency', currency });
const fmtDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
};
const titleCase = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);
const cardLabel = (c) => (c ? `${titleCase(c.brand)} •••• ${c.last4}` : '');

const DARK_BTN = { bgcolor: '#15171c', textTransform: 'none', fontWeight: 700, borderRadius: 2, '&:hover': { bgcolor: '#000' } };

// Upgrade flow, in two steps: pick a higher plan on the member's current
// interval ('select'), then review the prorated amount due now — credit for the
// current plan — and confirm ('review'). Confirming swaps the price in place,
// with a 3DS step only when the bank requires it.
const MembershipUpgradeDialogV2 = ({
  open, membership, onClose, onUpgraded,
}) => {
  const m = membership || {};
  const interval = m.billing_title || 'Annual';
  const curAmount = Number(m.amount_cents) || 0;

  const [plans, setPlans] = useState([]);
  const [loadingPlans, setLoadingPlans] = useState(false);
  const [card, setCard] = useState(null);
  const [selected, setSelected] = useState(null); // { plan, pricing }
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [upgrading, setUpgrading] = useState(false);
  const [error, setError] = useState('');
  const [step, setStep] = useState('select'); // 'select' (plan cards) | 'review' (payment)

  // (Re)load the higher plans + the card on file whenever the dialog opens.
  useEffect(() => {
    if (!open) return undefined;
    setSelected(null); setPreview(null); setError(''); setStep('select');
    let active = true;
    (async () => {
      setLoadingPlans(true);
      try {
        const token = getToken();
        const opts = token ? { params: { token } } : undefined;
        const [plansRes, pmRes] = await Promise.all([
          axios.get(`${baseUrl}/v4/membership-plans`, opts),
          axios.get(`${baseUrl}/v4/membership/payment-methods`, opts).catch(() => null),
        ]);
        if (!active) return;
        const data = plansRes?.data;
        if (data?.success && Array.isArray(data.data)) {
          // Only same-interval plans priced above the current one are upgrades.
          const eligible = data.data
            .map((p) => ({ plan: p, pricing: (p.pricing || []).find((pr) => pr.billing_title === interval) }))
            .filter((o) => o.pricing && o.pricing.id != null && Number(o.pricing.price_cents) > curAmount);
          setPlans(eligible);
        } else {
          setError('Could not load upgrade options.');
        }
        const cards = pmRes?.data?.data;
        if (Array.isArray(cards) && cards.length) setCard(cards.find((c) => c.is_default) || cards[0]);
      } catch (e) {
        if (active) setError('Could not load upgrade options.');
      } finally {
        if (active) setLoadingPlans(false);
      }
    })();
    return () => { active = false; };
  }, [open, interval, curAmount]);

  const choosePlan = async (opt) => {
    setSelected(opt); setPreview(null); setError('');
    setStep('review'); // advance to the payment step; preview loads there
    setPreviewing(true);
    try {
      const res = await dataFetcher('v4/membership/upgrade-preview', { membership_plan_price_id: opt.pricing.id });
      if (res?.success && res?.data) setPreview(res.data);
      else setError(res?.message || 'Could not preview this upgrade.');
    } catch (e) {
      setError(e?.response?.data?.message || 'Could not preview this upgrade.');
    } finally {
      setPreviewing(false);
    }
  };

  const confirmUpgrade = async () => {
    if (!selected) return;
    setUpgrading(true); setError('');
    try {
      const res = await dataFetcher('v4/membership/upgrade', { membership_plan_price_id: selected.pricing.id });
      const d = res?.data || {};
      if (d.status === 'requires_action' && d.client_secret) {
        const stripe = await stripePromise;
        if (!stripe) { setError('Payments are not configured.'); setUpgrading(false); return; }
        const { error: authErr } = await stripe.handleNextAction({ clientSecret: d.client_secret });
        if (authErr) { setError(authErr.message || 'Card authentication failed.'); setUpgrading(false); return; }
        onUpgraded?.(); return;
      }
      if (d.status === 'active') { onUpgraded?.(); return; }
      setError(res?.message || 'Upgrade failed — please try again.'); setUpgrading(false);
    } catch (e) {
      // BE returns 409 with a message for same-plan / downgrade / interval issues.
      setError(e?.response?.data?.message || 'Upgrade failed — please try again.');
      setUpgrading(false);
    }
  };

  const cur = preview?.currency || 'usd';
  // eslint-disable-next-line no-nested-ternary
  const confirmLabel = upgrading
    ? 'Upgrading…'
    : (!preview
      ? 'Upgrade'
      : (preview.amount_due_now_cents > 0
        ? `Pay ${money(preview.amount_due_now_cents, cur)} & upgrade`
        : 'Confirm upgrade'));

  return (
    <Dialog
      open={open}
      onClose={() => { if (!upgrading) onClose?.(); }}
      maxWidth={step === 'select' ? 'md' : 'sm'}
      fullWidth
    >
      <Box sx={{ p: { xs: 2, sm: 3 } }}>
        {step === 'select' ? (
          /* ---- Step 1: pick a plan ---- */
          <>
            <Typography sx={{ fontSize: 18, fontWeight: 800, color: '#15171c' }}>Upgrade your membership</Typography>
            <Typography sx={{ mt: 0.5, mb: 2, fontSize: 13.5, color: '#6b7280' }}>
              You&apos;re on
              {' '}
              <b>{m.plan_name || 'your current plan'}</b>
              {`. Pick a higher ${interval.toLowerCase()} plan — you'll only pay the prorated difference now, credited for the time left on your current plan.`}
            </Typography>

            {loadingPlans ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><CircularProgress size={24} /></Box>
            ) : plans.length === 0 ? (
              <Typography sx={{ color: '#6b7280', fontSize: 14, py: 2 }}>No upgrade options available right now.</Typography>
            ) : (
              /* Same card shape as the enrolment plans in MembershipPanelV2, so
                 upgrading and subscribing read identically. */
              <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 2 }}>
                {plans.map((opt) => {
                  const active = selected?.pricing.id === opt.pricing.id;
                  const showcase = opt.pricing.benefits?.showcase || [];
                  return (
                    <Box
                      key={opt.pricing.id}
                      role="button"
                      tabIndex={upgrading ? -1 : 0}
                      aria-pressed={active}
                      onClick={() => { if (!upgrading) choosePlan(opt); }}
                      onKeyDown={(e) => {
                        if (!upgrading && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); choosePlan(opt); }
                      }}
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        flex: '1 1 220px',
                        minWidth: 0,
                        maxWidth: { xs: '100%', sm: 300 },
                        p: 2,
                        borderRadius: 2.5,
                        border: '1px solid',
                        borderColor: active ? '#15171c' : '#e5e7eb',
                        bgcolor: active ? 'rgba(21,23,28,0.04)' : '#fff',
                        cursor: upgrading ? 'default' : 'pointer',
                        transition: 'border-color .15s ease, transform .15s ease, box-shadow .15s ease',
                        '&:hover': upgrading ? {} : {
                          borderColor: active ? '#15171c' : '#c0c6cf',
                          transform: 'translateY(-2px)',
                          boxShadow: '0 10px 24px rgba(16,24,40,0.1)',
                        },
                      }}
                    >
                      <Typography sx={{ fontSize: 16, fontWeight: 800, color: '#15171c' }}>{opt.plan.name}</Typography>
                      <Stack spacing={0.75} sx={{ flex: 1, mt: 1.25, mb: 2 }}>
                        {showcase.map((b, i) => (
                          // eslint-disable-next-line react/no-array-index-key
                          <Stack key={i} direction="row" spacing={0.75} sx={{ alignItems: 'flex-start' }}>
                            <CheckRoundedIcon sx={{ fontSize: 16, color: '#1f9d57', mt: '2px' }} />
                            <Typography sx={{ fontSize: 13, color: '#374151' }}>{b.title}</Typography>
                          </Stack>
                        ))}
                      </Stack>
                      {/* Price pinned to the card bottom so the cards line up. */}
                      <Typography sx={{
                        mt: 'auto', px: 1.5, py: 1, borderRadius: 2, bgcolor: 'rgba(21,23,28,0.05)',
                      }}
                      >
                        <Box component="span" sx={{ fontSize: 20, fontWeight: 800, color: '#15171c' }}>
                          {money(opt.pricing.price_cents, cur)}
                        </Box>
                        <Box component="span" sx={{ fontSize: 12, fontWeight: 700, color: '#9aa1ab' }}>{`/${interval}`}</Box>
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
            )}

            {error && <Typography sx={{ mt: 1.5, fontSize: 13, color: '#c0392b', fontWeight: 600 }}>{error}</Typography>}

            <Stack direction="row" spacing={1} sx={{ justifyContent: 'flex-end', mt: 2.5 }}>
              <Button onClick={onClose} disabled={upgrading} sx={{ textTransform: 'none', fontWeight: 700, color: '#6b7280' }}>Cancel</Button>
            </Stack>
          </>
        ) : (
          /* ---- Step 2: review + pay ---- */
          <>
            <Button
              onClick={() => { if (!upgrading) { setStep('select'); setSelected(null); setPreview(null); setError(''); } }}
              disabled={upgrading}
              startIcon={<ArrowBackRoundedIcon sx={{ fontSize: 16 }} />}
              sx={{
                mb: 1.5,
                px: 1.5,
                py: 0.5,
                borderRadius: 999,
                border: '1px solid #e5e7eb',
                bgcolor: '#f6f7fb',
                textTransform: 'none',
                fontWeight: 700,
                fontSize: 12.5,
                color: '#4b5563',
                '&:hover': { bgcolor: '#eef1f7', borderColor: '#c0c6cf', color: '#15171c' },
              }}
            >
              Plans
            </Button>
            <Typography sx={{ fontSize: 18, fontWeight: 800, color: '#15171c' }}>Confirm upgrade</Typography>

            {selected && (
              <Stack
                direction="row"
                sx={{
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 1.5,
                  mt: 1.5,
                  px: 2,
                  py: 1.25,
                  borderRadius: 2,
                  border: '1px solid #15171c',
                  bgcolor: 'rgba(21,23,28,0.04)',
                }}
              >
                <Typography sx={{ fontSize: 14.5, fontWeight: 700, color: '#15171c' }}>{selected.plan.name}</Typography>
                <Typography sx={{ fontSize: 14, fontWeight: 800, color: '#15171c' }}>
                  {money(selected.pricing.price_cents, cur)}
                  <Box component="span" sx={{ fontSize: 12, fontWeight: 600, color: '#9aa1ab' }}>{`/${interval}`}</Box>
                </Typography>
              </Stack>
            )}

            {previewing && <Typography sx={{ mt: 1.5, fontSize: 13.5, color: '#6b7280' }}>Calculating proration…</Typography>}
            {preview && !previewing && (
              <Box sx={{ mt: 2, p: 1.75, borderRadius: 2, bgcolor: '#f8f9fb', border: '1px solid #eef1f6' }}>
                <Stack direction="row" sx={{ justifyContent: 'space-between', fontSize: 13.5 }}>
                  <Typography sx={{ fontSize: 13.5, color: '#374151' }}>{`New plan (${preview.plan_name}, ${interval})`}</Typography>
                  <Typography sx={{ fontSize: 13.5, color: '#374151' }}>{money(preview.new_plan_amount_cents, cur)}</Typography>
                </Stack>
                <Stack direction="row" sx={{ justifyContent: 'space-between', mt: 0.5 }}>
                  <Typography sx={{ fontSize: 13.5, color: '#374151' }}>Credit for current plan</Typography>
                  <Typography sx={{ fontSize: 13.5, color: '#1f7a45', fontWeight: 700 }}>{`−${money(preview.proration_credit_cents, cur)}`}</Typography>
                </Stack>
                <Stack direction="row" sx={{ justifyContent: 'space-between', mt: 1, pt: 1, borderTop: '1px solid #e5e7eb' }}>
                  <Typography sx={{ fontSize: 14, fontWeight: 800, color: '#15171c' }}>Due now</Typography>
                  <Typography sx={{ fontSize: 14, fontWeight: 800, color: '#15171c' }}>{money(preview.amount_due_now_cents, cur)}</Typography>
                </Stack>
                {preview.next_renewal_date && (
                  <Typography sx={{ mt: 1, fontSize: 12.5, color: '#6b7280' }}>
                    {`Then ${money(preview.new_plan_amount_cents, cur)} on ${fmtDate(preview.next_renewal_date)}.`}
                  </Typography>
                )}
                <Typography sx={{ mt: 1, fontSize: 12.5, color: '#6b7280' }}>
                  {preview.amount_due_now_cents > 0
                    ? `${card ? cardLabel(card) : 'Your card on file'} will be charged.`
                    : `No charge today${card ? ` · ${cardLabel(card)} on file` : ''}.`}
                </Typography>
              </Box>
            )}

            {error && <Typography sx={{ mt: 1.5, fontSize: 13, color: '#c0392b', fontWeight: 600 }}>{error}</Typography>}

            <Stack direction="row" spacing={1} sx={{ justifyContent: 'flex-end', mt: 2.5 }}>
              <Button onClick={onClose} disabled={upgrading} sx={{ textTransform: 'none', fontWeight: 700, color: '#6b7280' }}>Cancel</Button>
              <Button
                variant="contained"
                disableElevation
                onClick={confirmUpgrade}
                disabled={!selected || !preview || previewing || upgrading}
                sx={DARK_BTN}
              >
                {confirmLabel}
              </Button>
            </Stack>
          </>
        )}
      </Box>
    </Dialog>
  );
};

export default MembershipUpgradeDialogV2;
